import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  IFContratoResumo,
  ProjecaoRisco,
  AlertaConcentracao,
} from '../types/financiamento.types';
import { useIFCarteiraResumo } from './useIFCarteira';
import { fetchRiskHistoricoByContratoIds, fetchAlertasPorContrato } from '../services/ifDataService';

export type { ProjecaoRisco, AlertaConcentracao };

export function useIFInteligencia() {
  const { resumos, isLoading, kpis } = useIFCarteiraResumo();

  const contratoIds = useMemo(() => resumos.map(r => r.contrato_id), [resumos]);

  const { data: historico = [] } = useQuery({
    queryKey: ['if-risk-historico-carteira', contratoIds],
    queryFn: () => fetchRiskHistoricoByContratoIds(contratoIds),
    enabled: contratoIds.length > 0,
  });

  const { data: alertasCount = [] } = useQuery({
    queryKey: ['if-alertas-por-contrato'],
    queryFn: fetchAlertasPorContrato,
  });

  // ── Projections (operational only) ─────────────────────────────
  const projecoes: ProjecaoRisco[] = useMemo(() => {
    return resumos.map(r => {
      const score = r.score_risco ?? 0;
      const diasRestantes = r.end_date
        ? Math.max(0, Math.ceil((new Date(r.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;

      const hist = historico.filter((h: any) => h.contrato_id === r.contrato_id);
      let trendPerDay = 0;
      if (hist.length >= 2) {
        const first: any = hist[0];
        const last: any = hist[hist.length - 1];
        const days = Math.max(1, (new Date(last.data_referencia).getTime() - new Date(first.data_referencia).getTime()) / (1000 * 60 * 60 * 24));
        trendPerDay = ((last.score_composto ?? 0) - (first.score_composto ?? 0)) / days;
      } else if (r.tendencia === 'piorando') {
        trendPerDay = 0.3;
      } else if (r.tendencia === 'melhorando') {
        trendPerDay = -0.2;
      }

      const progressRate = r.progresso_fisico_pct / Math.max(1, 100 - (diasRestantes / Math.max(1, diasRestantes + 30)) * 100);
      const progressFactor = progressRate < 0.8 ? 1.2 : progressRate > 1.2 ? 0.8 : 1;

      const proj30 = Math.max(0, Math.min(100, score + trendPerDay * 30 * progressFactor));
      const proj60 = Math.max(0, Math.min(100, score + trendPerDay * 60 * progressFactor));
      const proj90 = Math.max(0, Math.min(100, score + trendPerDay * 90 * progressFactor));

      return {
        contrato_id: r.contrato_id,
        contrato_nome: r.contrato_nome,
        score_atual: score,
        tendencia: r.tendencia,
        dias_restantes: diasRestantes,
        proj_30d: Math.round(proj30 * 10) / 10,
        proj_60d: Math.round(proj60 * 10) / 10,
        proj_90d: Math.round(proj90 * 10) / 10,
        risco_crescente: proj90 > score + 5,
      };
    }).sort((a, b) => b.proj_90d - a.proj_90d);
  }, [resumos, historico]);

  // ── Concentration Alerts (operational) ─────────────────────────
  const alertasConcentracao: AlertaConcentracao[] = useMemo(() => {
    const alerts: AlertaConcentracao[] = [];
    const total = resumos.length;
    if (total === 0) return alerts;

    const byEmp = new Map<string, number>();
    resumos.forEach(r => {
      byEmp.set(r.contratada, (byEmp.get(r.contratada) || 0) + 1);
    });
    byEmp.forEach((count, emp) => {
      const pct = (count / total) * 100;
      if (pct > 50) {
        alerts.push({ tipo: 'empreiteira', mensagem: `${emp} concentra ${pct.toFixed(0)}% dos contratos (>${50}%)`, severidade: 'critico', valor_pct: pct });
      } else if (pct > 35) {
        alerts.push({ tipo: 'empreiteira', mensagem: `${emp} concentra ${pct.toFixed(0)}% dos contratos (>${35}%)`, severidade: 'atencao', valor_pct: pct });
      }
    });

    const contratosAltoRisco = resumos.filter(r =>
      r.classificacao_risco === 'crítico' || r.classificacao_risco === 'critico' || r.classificacao_risco === 'alto'
    ).length;
    const pctRisco = (contratosAltoRisco / total) * 100;
    if (pctRisco > 50) {
      alerts.push({ tipo: 'risco', mensagem: `${pctRisco.toFixed(0)}% dos contratos em risco alto/crítico`, severidade: 'critico', valor_pct: pctRisco });
    } else if (pctRisco > 30) {
      alerts.push({ tipo: 'risco', mensagem: `${pctRisco.toFixed(0)}% dos contratos em risco alto/crítico`, severidade: 'atencao', valor_pct: pctRisco });
    }

    return alerts;
  }, [resumos]);

  return {
    resumos,
    isLoading,
    kpis,
    projecoes,
    alertasConcentracao,
  };
}
